// Hooks de mutaciones para autenticación con React Query
// Maneja las operaciones de login, logout y registro con Supabase
// Incluye mutaciones para gestión de perfil de usuario

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { 
  verifyUserPassword, 
  verifyUserEmail, 
  updateUserEmail, 
  updateUserPassword, 
  deactivateUserAccount, 
  getCurrentUser,
  signOut 
} from '../services/authService';

// Hook para manejar el login de usuarios
export const useAuthMutations = () => {
  const queryClient = useQueryClient();

  // Mutación para login
  const loginMutation = useMutation({
    mutationFn: async ({ email, password, rememberMe = false }) => {
      // Guardamos la preferencia de "Recordarme" en localStorage
      localStorage.setItem('rememberMe', rememberMe.toString());
      
      // Si el usuario quiere ser recordado, guardamos el email
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        // Limpiamos el email guardado si no quiere ser recordado
        localStorage.removeItem('rememberedEmail');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      // Invalida y refresca la query de sesión
      queryClient.invalidateQueries(['auth', 'session']);
      toast.success('¡Inicio de sesión exitoso!');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al iniciar sesión');
    },
  });

  // Mutación para logout
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      // Limpia todas las queries relacionadas con autenticación
      queryClient.clear();
      toast.success('Sesión cerrada correctamente');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al cerrar sesión');
    },
  });

  // Mutación para registro de nuevos usuarios
  const registerMutation = useMutation({
    mutationFn: async ({ email, password, userData }) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData || {},
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: async (data, variables) => {
      // Envía emails de bienvenida y notificación al administrador
      // usando la Edge Function send-email existente
      const userName = variables.userData?.full_name || 'Usuario';
      const userEmail = variables.email;
      
      // Genera HTML del email de bienvenida
      const welcomeHTML = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #8B5CF6; margin: 0;">¡Bienvenido a Amarsi! 🎉</h1>
          </div>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333;">
            Hola <strong>${userName}</strong>,
          </p>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333;">
            ¡Qué emoción que te hayas unido a nuestra comunidad! Amarsi es una plataforma en constante crecimiento donde podrás conectar con personas increíbles.
          </p>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333;">
            Estamos aquí para ayudarte a encontrar lo que buscas, ya sea amistad, algo casual o una relación seria. Tu viaje comienza ahora.
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <p style="font-size: 18px; font-weight: bold; color: #8B5CF6;">
              ¡Sé auténtico, sé valiente, sé tú! 💜
            </p>
          </div>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333;">
            Si tienes alguna pregunta, no dudes en contactarnos. Estamos aquí para ti.
          </p>
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #666; font-size: 14px;">
            <p>El equipo de Amarsi</p>
            <p style="margin: 5px 0;">contact@clubamarsi.com</p>
          </div>
        </div>
      `;

      // Genera HTML del email de notificación al administrador
      const adminHTML = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #8B5CF6; margin: 0;">Nuevo Usuario Registrado</h1>
          </div>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333;">
            Se ha registrado un nuevo usuario en la plataforma Amarsi.
          </p>
          
          <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 10px 0; font-size: 16px; color: #333;">
              <strong>Nombre:</strong> ${userName}
            </p>
            <p style="margin: 10px 0; font-size: 16px; color: #333;">
              <strong>Email:</strong> ${userEmail}
            </p>
          </div>
          
          <p style="font-size: 16px; line-height: 1.6; color: #333;">
            Revisa el panel de administración para más detalles.
          </p>
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #666; font-size: 14px;">
            <p>Notificación automática de Amarsi</p>
          </div>
        </div>
      `;

      // Llama a la Edge Function send-email existente para enviar ambos emails
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Envía email de bienvenida al usuario
        await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({
            to: userEmail,
            subject: '¡Bienvenido a Amarsi! 🎉',
            html: welcomeHTML,
          }),
        });

        // Envía notificación al administrador
        await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({
            to: 'darox089@gmail.com',
            subject: 'Nuevo usuario registrado',
            html: adminHTML,
          }),
        });
      } catch (error) {
        console.error('Error enviando emails de registro:', error);
        // No interrumpimos el flujo si falla el envío de emails
      }

      if (data.user && !data.session) {
        // Usuario creado pero necesita confirmación por email
        toast.success('Cuenta creada. Revisa tu email para confirmar.');
      } else if (data.session) {
        // Usuario creado y sesión iniciada (email auto-confirmado)
        queryClient.invalidateQueries(['auth', 'session']);
        toast.success('¡Cuenta creada e iniciada sesión!');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear cuenta');
    },
  });

  return {
    loginMutation,
    logoutMutation,
    registerMutation,
  };
};

/**
 * Hook para verificar la contraseña del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useVerifyPasswordMutation = (options = {}) => {
  return useMutation({
    mutationFn: verifyUserPassword,
    onSuccess: (data, variables) => {
      console.log(' --- useVerifyPasswordMutation(data)', data);
      // La verificación solo llega aquí si la contraseña es correcta
      console.log('Contraseña verificada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error verificando contraseña:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para verificar si un email ya está en uso
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useVerifyEmailMutation = (options = {}) => {
  return useMutation({
    mutationFn: verifyUserEmail,
    onSuccess: (data, variables) => {
      console.log('Verificación de email completada');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error verificando email:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para actualizar el email del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useUpdateEmailMutation = (options = {}) => {
  return useMutation({
    mutationFn: updateUserEmail,
    onSuccess: (data, variables) => {
      console.log('Email actualizado exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error actualizando email:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para actualizar la contraseña del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useUpdatePasswordMutation = (options = {}) => {
  return useMutation({
    mutationFn: updateUserPassword,
    onSuccess: (data, variables) => {
      console.log('Contraseña actualizada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error actualizando contraseña:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para desactivar la cuenta del usuario
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useDeactivateAccountMutation = (options = {}) => {
  return useMutation({
    mutationFn: deactivateUserAccount,
    onSuccess: (data, variables) => {
      console.log('Cuenta desactivada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error desactivando cuenta:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para obtener información del usuario actual
 * @param {object} options - Opciones adicionales del query
 * @returns {object} - Query de TanStack Query
 */
export const useCurrentUserQuery = (options = {}) => {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutos
    onSuccess: (data) => {
      console.log('Usuario obtenido exitosamente');
      options.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('Error obteniendo usuario:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Hook para cerrar sesión
 * @param {object} options - Opciones adicionales del mutation
 * @returns {object} - Mutation de TanStack Query
 */
export const useSignOutMutation = (options = {}) => {
  return useMutation({
    mutationFn: signOut,
    onSuccess: (data, variables) => {
      console.log('Sesión cerrada exitosamente');
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      console.error('Error cerrando sesión:', error);
      options.onError?.(error);
    },
    ...options
  });
};
